import {
  Mesh,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  AmbientLight,
  MeshStandardMaterial,
  Object3D,
  SpriteMaterial,
  Sprite,
} from "three";

import { ResourcesOptions, commonOption, XYZ } from "./resources";
import TWEEN from "@tweenjs/tween.js";
import { AllRes } from "./utils";

class SuperMountain {
  mode: number = 0;
  renderer: WebGLRenderer;
  animationFrameId: number = 0;
  camera: PerspectiveCamera;
  mountain: Object3D;
  mountainTop: Mesh;
  mountainBody: Mesh;
  scene: Scene;

  onTweening: boolean = false;

  constructor(
    canvasElement: HTMLCanvasElement,
    public res: ResourcesOptions,
    public loaderManager: AllRes
  ) {
    // 资源和配置
    this.res = res;

    // 初始化渲染器
    this.renderer = new WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas: canvasElement,
    });
    this.scene = new Scene();

    const light = new AmbientLight("white", 2);
    this.scene.add(light);

    // 添加各个部件
    this.camera = new PerspectiveCamera(); // 视角
    this.camera.up.set(0, 0, 1)
    this.scene.add(this.camera);
    this.setCamera();

    this.mountain = new Mesh();
    this.mountainTop = new Mesh(); // 山头
    this.mountainBody = new Mesh(); // 山体
    this.setMountain();

    this.setCloud();

    // 更新大小
    this.updateSize();
  }
  // 循环器
  animate(time?): void {
    this.render(time);
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }
  // 渲染部分
  render(time): void {
    this.renderer.render(this.scene, this.camera);
    TWEEN.update(time);
  }

  setMountain() {
    this.loaderManager.mountainModel.then((model) => {
      this.mountain = model
      this.scene.add(model);

      this.mountainTop = (this.mountain.children[1] as Mesh)
      this.mountainBody = (this.mountain.children[0] as Mesh)

      this.mountainTop.material = new MeshStandardMaterial({
        map: this.loaderManager.mountainTextureTop,
        alphaMap: this.loaderManager.mountainMaskTop,
        transparent: true
      });
      this.mountainBody.material = new MeshStandardMaterial({
        map: this.loaderManager.mountainTextureBody,
        alphaMap: this.loaderManager.mountainMaskBody,
        transparent: true
      });

    });
  }
  cloudMaterial: SpriteMaterial | null = null
  setCloud() {
    this.cloudMaterial = new SpriteMaterial({
      map: this.loaderManager.cloudTexture,
      transparent: true,
      opacity: 0
    });

    const cloudBg = new Sprite(this.cloudMaterial);
    cloudBg.scale.set(10, 10, 10)
    cloudBg.position.set(1, 3, 3)

    const cloud = new Sprite(this.cloudMaterial);
    cloud.scale.set(10, 10, 10)
    cloud.position.set(1, -5, -3)

    this.scene.add(cloudBg);
    this.scene.add(cloud);
  }
  // 视角设置
  setCamera() {
    // 设置默认值
    const mode = this.res.modes[this.mode];
    const cameraPos = { ...mode.cameraPosition };
    const cameraLookAt = { ...mode.cameraLookAt };

    this.camera.position.set(cameraPos.x, cameraPos.y, cameraPos.z);
    this.camera.lookAt(cameraLookAt.x, cameraLookAt.y, cameraLookAt.z);

    this.camera.far = commonOption.CAMERA_FAR;
    this.camera.fov = commonOption.CAMERA_FOV;
    this.camera.near = commonOption.CAMERA_NEAR;
  }
  // 更新视角大小
  updateSize(): void {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
  nextMode() {
    const beforeMode = this.mode;
    if (this.mode === 0) // aod -> lock
      this.mode = 1;
    else if (this.mode === 1) // lock -> home
      this.mode = 2;
    else if (this.mode === 2) // home -> lock
      this.mode = 3;
    else if (this.mode === 3) // lock -> aod
      this.mode = 0;

    const modeBefore = this.res.modes[beforeMode];
    const modeNow = this.res.modes[this.mode];
    const duration = this.res.tweenDuration;

    const opacityTween = new TWEEN.Tween({ o: beforeMode === 0 ? 0 : 1 });
    const cameraPosAtTween = new TWEEN.Tween({ ...modeBefore.cameraPosition });
    const cameraLookAtTween = new TWEEN.Tween({ ...modeBefore.cameraLookAt });

    cameraPosAtTween
      .easing(TWEEN.Easing.Cubic.InOut)
      .onUpdate((o: XYZ) => this.camera.position.set(o.x, o.y, o.z))
      .onStart(() => this.onTweening = true)
      .onComplete(() => this.onTweening = false)
      .onStop(() => this.onTweening = false);

    cameraLookAtTween.easing(TWEEN.Easing.Cubic.InOut).onUpdate((o: XYZ) => {
      this.camera.lookAt(o.x, o.y, o.z);
    });
    opacityTween.onUpdate(({ o }: {o: number}) => {
      const m = document.querySelector('.bg-mountain') as HTMLDivElement
      if(m) m.style.opacity = o.toString()
      if (this.cloudMaterial) this.cloudMaterial.opacity = o * 0.3
    });

    cameraPosAtTween.to({ ...modeNow.cameraPosition }, duration).start();
    cameraLookAtTween.to({ ...modeNow.cameraLookAt }, duration).start();
    opacityTween.to({ o: this.mode === 0 ? 0 : 1 }, duration).start();
    
    return this.mode;
  }
}
export default SuperMountain;
